// Scanned value by month — what was scanned each month (the day the ticket was
// scanned, not a projection), as monthly bars from the first scan. Reads every scanned
// quote the page already loaded; the old chart read the newest 100 only, so
// earlier months came out short. The current month is marked "to date".
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { money, quoteTotal } from './revivalFormat';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Every calendar month from the first scan to now (at least 6, at most 24),
 *  oldest first. A month with no scans is a real 0 (nothing was scanned), so the
 *  bars never skip a gap the way a line through only the busy months did
 *  (Oct · Nov · Mar read as neighbours). */
export const monthlyScanned = (quotes, now = new Date()) => {
  const firsts = quotes.map((q) => new Date(q.created_at)).filter((d) => !Number.isNaN(d.getTime()));
  const first = firsts.length ? new Date(Math.min(...firsts)) : now;
  const span = (now.getFullYear() - first.getFullYear()) * 12 + now.getMonth() - first.getMonth() + 1;
  const months = Math.max(6, Math.min(24, span));
  const out = [];
  for (let k = months - 1; k >= 0; k -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - k, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    out.push({ key, month: `${MONTH_NAMES[d.getMonth()]} ${String(d.getFullYear()).slice(2)}${k === 0 ? ' (to date)' : ''}`, value: 0, quotes: 0 });
  }
  const at = Object.fromEntries(out.map((b) => [b.key, b]));
  for (const q of quotes) {
    const d = new Date(q.created_at);
    if (Number.isNaN(d.getTime())) continue;
    const b = at[`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`];
    if (!b) continue;
    b.value += quoteTotal(q) ?? 0;
    b.quotes += 1;
  }
  return out.map((b) => ({ ...b, value: Math.round(b.value) }));
};

const PlanChart = ({ data }) => {
  if (!data.some((b) => b.quotes)) {
    return <p className="rv-note">Nothing scanned yet. Scan a ticket and its value shows here by month.</p>;
  }
  return (
    <div style={{ width: '100%', height: 240 }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <XAxis dataKey="month" stroke="var(--text-3)" tickLine={false} fontSize={11} interval={0} />
          <YAxis stroke="var(--text-3)" tickLine={false} fontSize={11} width={52} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
          <Tooltip cursor={{ fill: 'var(--bg-hover)' }} formatter={(v, _n, p) => [`${money(v)} · ${p?.payload?.quotes ?? 0} quotes`, 'Scanned']} />
          <Bar dataKey="value" fill="var(--text-2)" radius={[3, 3, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PlanChart;
